import { createRef, PureComponent } from 'react';
import type { ReactNode, ReactElement, RefObject } from 'react';
import track from 'react-tracking';
import type { SubNavRoute } from '@/constants/routes';
import { Button } from '@simplywallst/ui-core';
import { Navigation, ActionWrapper, MobileTopbarSubNavWrapper } from './styled';
import { LayoutConsumer } from '@components/Layout/context/LayoutContext';

const easeInOutCubic = (t, b, c, d) => {
  t /= d / 2;
  if (t < 1) return (c / 2) * t * t * t + b;
  t -= 2;
  return (c / 2) * (t * t * t + 2) + b;
};

export interface Props {
  // This is optionally a readonly array, so that routes arrays with constant values can be used
  // (e.g. `[{ anchor: 'COMPANY_SUMMARY' }] as const`)
  routes: SubNavRoute[] | ReadonlyArray<SubNavRoute>;
  selected: string;
  actionLabel?: string;
  actionIcon?: ReactNode;
  onSelect?: (route: string, payload: ReducerActionPayload) => void;
  handleActionClick?: () => void;
  children?: (
    props: ChildrenProps
  ) => ReactElement<ChildrenProps>[] | ReactElement<ChildrenProps>;
  renderInPortal?: boolean;
}

interface ChildrenProps {
  routes: SubNavRoute[];
  refMap: any;
  selected: string;
  handleClick: (
    _label: ReactNode,
    route?: string,
    payload?: ReducerActionPayload
  ) => void;
}

@track({
  type: 'track',
  subject: 'MobileTopbarSubNav',
})
class MobileTopbarSubNav extends PureComponent<Props> {
  private list: RefObject<HTMLUListElement>;
  private listWrapper: RefObject<HTMLElement>;
  private refMap = {};

  constructor(props) {
    super(props);
    this.list = createRef<HTMLUListElement>();
    this.listWrapper = createRef<HTMLElement>();
    this.refMap = this.props.routes.reduce((a, b, i) => {
      return {
        ...a,
        [i]: createRef<HTMLElement>(),
      };
    }, {} as SubNavRoute);
  }

  componentDidMount() {
    const { shouldComponentScroll } = this;
    const { selected } = this.props;
    if (selected) {
      shouldComponentScroll(selected);
    }
  }

  componentDidUpdate(prevProps) {
    const { shouldComponentScroll } = this;
    const { selected: prevSelected } = prevProps;
    const { selected: nextSelected } = this.props;
    if (nextSelected !== undefined && nextSelected !== prevSelected) {
      shouldComponentScroll(nextSelected);
    }
  }

  shouldComponentScroll = (route: string) => {
    const { handleScroll, refMap } = this;
    const { routes } = this.props;
    if (route !== undefined) {
      const index = routes.findIndex(
        ({ to, href, anchor }) => (to || href || anchor) === route
      );
      if (index >= 0 && index in refMap) {
        const { current } = refMap[index];
        if (current) handleScroll(current.offsetLeft);
      }
    }
  };

  handleAnimation = (e, s, d, t = 20, n = 0) => {
    const { handleAnimation } = this;
    if (e && n < t) {
      e.scrollLeft = easeInOutCubic(n, s, d, t);
      RUNTIME_ENV !== 'server' &&
        window !== null &&
        window.requestAnimationFrame(() => handleAnimation(e, s, d, t, n + 1));
    } else {
      e.scrollLeft = s + d;
    }
  };

  handleScroll = (destination) => {
    const {
      list: { current: list },
      handleAnimation,
      listWrapper: { current: listWrapper },
    } = this;
    if (list && listWrapper) {
      const scrollMax = list.scrollWidth - listWrapper.offsetWidth;
      const currentPos = list.scrollLeft;
      const offset = 50;
      if (destination === currentPos) return false;
      //	Stop anything positioned greater than the max scroll area
      if (destination >= scrollMax) {
        destination = scrollMax + offset;
      }
      //	Calculate the journey difference
      destination -= currentPos + offset;
      //	If there is no difference
      if (destination === 0) return false;
      handleAnimation(list, currentPos, destination);
    }
  };

  @track((props, state, [modifier]) => ({
    action: 'click',
    modifier: modifier,
  }))
  handleClick = (
    _label: ReactNode,
    route = '',
    payload: ReducerActionPayload = {}
  ) => {
    const { onSelect } = this.props;
    if (onSelect && route.length > 0) {
      onSelect(route, payload);
    }
    return true;
  };

  render() {
    const { handleClick, listWrapper, list, refMap } = this;
    const {
      routes,
      selected,
      handleActionClick,
      actionLabel,
      actionIcon,
      children,
      renderInPortal = true,
    } = this.props;

    if (!routes || routes?.length === 0) return null;

    const mobileNav = (
      <MobileTopbarSubNavWrapper data-cy-id="mobile-nav">
        <Navigation
          ref={listWrapper}
          hasAction={
            actionLabel !== undefined && handleActionClick !== undefined
          }
        >
          <ul
            className="overflow-x-auto overflow-y-hidden whitespace-nowrap text-left md:scrollbar"
            ref={list}
          >
            {children &&
              children({
                // Cast this to a non-readonly version of itself, as not all consumers can deal with the type
                // change added above for constant route arrays. Ideally the types would be passed through to
                // the child function props.
                routes: routes as SubNavRoute[],
                selected,
                handleClick,
                refMap,
              })}
          </ul>
        </Navigation>
        {actionLabel && handleActionClick && (
          <ActionWrapper>
            {actionIcon ? (
              <Button
                styleType="louder"
                alignIcon="left"
                onClick={() => handleActionClick()}
              >
                {actionIcon}
                {actionLabel}
              </Button>
            ) : (
              <Button styleType="louder" onClick={() => handleActionClick()}>
                {actionLabel}
              </Button>
            )}
          </ActionWrapper>
        )}
      </MobileTopbarSubNavWrapper>
    );

    return renderInPortal ? (
      <LayoutConsumer>{mobileNav}</LayoutConsumer>
    ) : (
      <>{mobileNav}</>
    );
  }
}

export default MobileTopbarSubNav;
